import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { Client } from '@ooh/contracts';
import type { SearchableSelectFieldProps } from '@ooh/ui/components/searchable-select-field';
import { ClientDialog } from '../../clients/client-dialog';

type ClientFieldAction = NonNullable<SearchableSelectFieldProps['action']>;

export interface UseInlineClientCreateActionResult {
  /** Spread onto the Klient `SearchableSelectField`'s `action` prop. */
  action: ClientFieldAction;
  /** Render alongside the field — the create/select-existing-suggestion dialog. */
  dialog: React.ReactNode;
}

/**
 * Surfaces "create a new client" as an always-visible row inside the Klient
 * dropdown itself (CU-86cbc2qv8 UX follow-up), instead of a disconnected
 * button below the field — the row a user reaches for the instant a search
 * doesn't find their client is right where they were already looking.
 * Shared between the quick-add picker and the full create-offer form so
 * both wire the same action + dialog instead of parallel implementations.
 */
export function useInlineClientCreateAction(params: {
  canCreateClient: boolean;
  /**
   * `wasCreated` is `true` when the client was just created (`ClientDialog`'s
   * `onSuccess`) and `false` when an existing client was picked from the
   * name-search suggestions (`onSelectExisting`) — lets callers gate
   * create-only behavior (e.g. single-brand auto-select) accordingly.
   */
  onClientCreated: (client: Client, wasCreated: boolean) => void;
}): UseInlineClientCreateActionResult {
  const { t } = useTranslation('offers');
  const [showDialog, setShowDialog] = React.useState(false);
  const { canCreateClient, onClientCreated } = params;

  const action = React.useMemo<ClientFieldAction>(
    () => ({
      label: t('create.inlineClient.showAction'),
      onSelect: () => setShowDialog(true),
      disabled: !canCreateClient,
      hint: canCreateClient ? undefined : t('create.inlineClient.noPermission'),
      testId: 'show-create-client-dialog',
    }),
    [canCreateClient, t],
  );

  const dialog = showDialog ? (
    <ClientDialog
      mode="create"
      onClose={() => setShowDialog(false)}
      onSuccess={(client) => onClientCreated(client, true)}
      onSelectExisting={(client) => onClientCreated(client, false)}
    />
  ) : null;

  return { action, dialog };
}
